/**
 * THE CARD DERIVED FROM THE OBJECT — pure. No React, no I/O.
 *
 * ⭐ THE DIRECTION IS THE WHOLE FIX. `GoverningBodies.jsx` builds its card list
 * from the five registry arrays and joins records ONTO those cards, so a body
 * the registry does not hold never enters the loop: it is not filtered out, it
 * never arrives. That is why the Pac-12 could hold six championships with a rep
 * accountable for them and be invisible from every direction at once, and it is
 * why 467 body labels carrying 3,077 records have no card — measured 2026-10-14
 * by a full 159-page page-through of all 15,854 records, not off the cache.
 *
 * So the registry stops being the denominator. THE OBJECT IS. A body that holds
 * records IS a body, whatever the registry says, and every distinct body value
 * present on a record gets a card here.
 *
 * ⚠️ IT IS BUILT ON `bodyCoverage()` RATHER THAN BESIDE IT, DELIBERATELY.
 * Body Coverage already computes this partition and already reconciles it, and
 * two modules counting "bodies with no card" separately is how one page comes
 * to disagree with another about the same number. This one classifies that
 * module's own `noCard` rows and adds nothing to the arithmetic.
 *
 * ⚠️ AND IT KEYS ON `sr_conference`, WHICH IS NOT AN ARBITRARY CHOICE.
 * `matcherFor()` in lib/bodyChampionships.js — the join every existing card
 * uses — reads `sr_conference` and the championship title. It never reads
 * `governing_body`. Keying the derived cards on anything else would mean the
 * cards and the reconciliation were counting different fields, and the
 * arithmetic would stop closing. `governing_body` is measured here all the
 * same, as `fusedKeys` and `qualified`, because the fused jurisdictions live on
 * that field and have to be SAID rather than quietly excluded.
 *
 * ⚠️ NO PERCENTAGE, HERE OR ANYWHERE DOWNSTREAM. Same reason BodyCoverage.jsx
 * refuses one: "how covered are we" has three different denominators and this
 * module can see two of them.
 */
import { bodyCoverage } from './bodyCoverage.js'
import { bareOf, KNOWN_COLLISIONS } from './bodyMap.js'

const S = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim())
const L = (v) => S(v).toLowerCase()

/* ── what makes a record unrateable ───────────────────────────────────────── *
 * ⚠️ A COUNT IS NOT COVERAGE. Lane zerofix measured 282 records whose
 * championship_name is either the body's own name or placeholder text, and 22
 * bodies that hold NOTHING ELSE. Those bodies pass any record-count test and
 * hold nothing a team could buy a ring for, so a card reading "21 records" that
 * are all placeholder text is a new kind of lie — the exact kind this surface
 * was built to stop. Every card therefore carries its unrateable count, and a
 * card that is entirely unrateable says so instead of showing a total. */
const PLACEHOLDER = /^(unresolved|tbd|test|placeholder|unknown|n\/a|none)\b|identical records|no field separates/i

/**
 * 🔴 ONE OF THE THREE KINDS CANNOT BE SEEN FROM THIS SURFACE, AND SAYING SO IS
 * THE WHOLE POINT OF THE RULE IT BELONGS TO.
 *
 * Lane zerofix measured 282 unrateable records on 2026-10-14: 112 placeholder
 * text and 170 whose `championship_name` IS THE BODY'S OWN NAME. This function
 * first tried to detect both, shipped, and then reported 21 Wolverine-Hoosier
 * records as unrateable in a node harness and NOT on the live page. The
 * difference was not a bug in either: measured against the object, all 21 store
 * `championship_name = "Wolverine-Hoosier Athletic Conference"` with
 * `sr_championship_name` EMPTY on all 15,854 records — but what reaches this
 * component is "Wolverine-Hoosier Athletic Conference — Tennis", because the
 * feed composes a display name from the body and the sport before any view sees
 * it. The record has a sport, a date and a division; the bare stored name is a
 * STORED-DATA defect that the product already papers over on the way out.
 *
 * So this surface genuinely cannot answer that question, and a check that
 * quietly returned nothing for it would be a silent zero of exactly the kind
 * this page exists to end — worse, one dressed as a clean bill of health. The
 * branch is removed rather than left to no-op, and the absence is RENDERED:
 * see UNRATEABLE_BLIND_SPOT below. That class is measurable against the object
 * (lane zerofix, stubs.json) and must be measured there, not here.
 */
export const UNRATEABLE_BLIND_SPOT =
  'This counts placeholder text and missing names only. It cannot see the 170 records whose stored '
  + 'championship_name is the body\'s own name, because the feed composes a display name from the body '
  + 'and the sport before this page reads it — so the defect is invisible from here by the time it '
  + 'arrives. That class is real and was measured against the object, not against this surface.'

export function unrateableKind(raw) {
  const name = S(raw?.championship_name)
  if (!name) return 'no-name'
  if (PLACEHOLDER.test(name)) return 'placeholder'
  return ''
}

/* ── event promoters, brands and series — NOT sanctioning bodies ─────────── *
 * These records still need somewhere to live and be worked, so they get a card.
 * The card must never say the promoter CROWNS anything.
 *
 * ⚠️ THE LIST IS WRITTEN DOWN RATHER THAN INFERRED, and that is a decision with
 * a cost either way. An inferred list is only as good as the data it was
 * inferred from; a hand list goes stale. A promoter missing from it lands in
 * `body-missing-row`, which is the safe direction: that bucket's card claims
 * only that the label is NAMED on N records, never that it crowns them. */
export const PROMOTERS = [
  'varsity spirit', 'varsity all star', 'nca all', 'uca ', 'uda ', 'nda ', 'jamz',
  'cheersport', 'jamfest', 'jam brands', 'summit', 'spirit celebration', 'spirit unlimited',
  'worldwide spirit', 'one finals', 'american cheer', 'athletic championships',
  'usasf', 'u.s. all star', 'all star federation', 'cheer ltd', 'epic brands', 'mardi gras spirit',
  'perfect game', 'pop warner', 'premier girls fastpitch', 'pgf', 'dixie youth', 'dixie boys',
  'babe ruth', 'american legion', 'little league', 'usssa', 'triple crown', 'aau',
  'bands of america', 'music for all', 'winter guard international', 'wgi', 'drum corps', 'dci',
  'fame events', 'rec foundation', 'white marlin', 'world series of', 'route 66',
  'bisbee', 'big rock', 'newport bermuda', 'rolex', 'battle of the bands', "world's strongest",
]
export const isPromoter = (label) => {
  const l = ` ${L(label)} `
  return PROMOTERS.some((p) => l.includes(p))
}

/** The known-collider acronym this label resolves to, or ''. */
export const collisionKey = (label) => {
  const b = bareOf(label).toUpperCase().replace(/[^A-Z0-9]/g, '')
  return KNOWN_COLLISIONS[b] ? b : ''
}

const wordTokens = (s) => L(s).replace(/&/g, ' and ').split(/[^a-z0-9]+/).filter(Boolean)

/**
 * The registry row this label is a DIFFERENT SPELLING OF, or null.
 *
 * 🔴 THE FIRST VERSION OF THIS TEST WAS A TOKEN-SUBSET AND IT WAS WRONG IN THE
 * EXPENSIVE DIRECTION. It called "USA Hockey" an alias of "Conference USA" and
 * "Chicagoland Collegiate Athletic Conference" an alias of the NCAA, because a
 * stopword list ate "conference" and left a one-token overlap deciding whether
 * a governing body exists. Measured 2026-10-14 against the live object: 168
 * bodies and 1,170 records were about to be written off as spelling drift,
 * against the 24 bodies / 248 records the corrected test actually finds. A loose alias rule does not merely misfile a
 * body: it answers "is there a gap here" with NO.
 *
 * So the test is word-aligned containment of the whole normalised string, or a
 * single-token acronym drift of at most three characters — the shapes actually
 * measured on this object (GAPPS's ampersand, NYSAIS against NYSAISAA, 3C2A,
 * RSEQ against RSEQ-U, Canada West, Empire 8). Neither can fire on
 * "Sun Conference" inside "ASUN Conference", where the boundary falls inside a
 * word: that is the "CAA Football" inside "NCAA Football" shape that mis-cleared
 * a body earlier today, and a word-boundary regex cannot catch it either.
 *
 * ⚠️ AND A WORD-ALIGNED SUFFIX IS NOT AN ALIAS. "North American Hockey League"
 * ends in "American Hockey League" and is a different league; "American Midwest
 * Conference" ends in "Midwest Conference" and is a different conference. A
 * LEADING qualifier is how organisations are distinguished, not how they are
 * abbreviated. The one honest exception is a parenthesised acronym at the end of
 * the registry's own name.
 */
export function aliasOf(label, regEntries) {
  const lw = wordTokens(label)
  if (!lw.length) return null
  const lj = lw.join(' ')
  const ck = collisionKey(label)
  for (const e of regEntries) {
    for (const cand of [e.name, e.acronym]) {
      if (!cand) continue
      const cw = wordTokens(cand)
      if (!cw.length) continue
      if (cw.join(' ') === lj && L(cand) !== L(label)) {
        return { label: cand, tab: e.tab, how: 'punctuation or ampersand' }
      }
      const short = lw.length <= cw.length ? lw : cw
      const long = lw.length <= cw.length ? cw : lw
      if (short.length < long.length
        && (short.length >= 2 || short[0].length >= 4)
        && short.every((w, i) => long[i] === w)
        && !(ck && collisionKey(cand) === ck)) {
        return { label: cand, tab: e.tab, how: 'word-aligned prefix' }
      }
      if (short.length === 1 && short[0].length >= 4 && long.length > 1
        && long.join(' ').endsWith(` ${short[0]}`)
        && new RegExp(`\\(\\s*${short[0]}\\s*\\)`, 'i').test(L(lw.length <= cw.length ? cand : label))) {
        return { label: cand, tab: e.tab, how: 'parenthesised acronym' }
      }
      if (lw.length === 1 && cw.length === 1) {
        const a = lw[0], b = cw[0]
        const s = a.length <= b.length ? a : b
        const t = a.length <= b.length ? b : a
        if (s !== t && s.length >= 5 && t.startsWith(s) && t.length - s.length <= 3) {
          return { label: cand, tab: e.tab, how: 'acronym drift' }
        }
      }
    }
  }
  return null
}

/**
 * A registry label this one merely sits INSIDE, with no token agreement.
 * Flagged, NEVER used to clear, merge or dismiss a body.
 */
export function substringRisk(label, regLabels) {
  const b = L(bareOf(label))
  if (b.length < 3 || b.length > 24) return null
  const lt = wordTokens(label)
  for (const reg of regLabels) {
    if (reg === b) continue
    if (reg.includes(b) && !lt.every((w) => wordTokens(reg).includes(w))) return reg
  }
  return null
}

/* ── the buckets, each with the sentence a card renders ──────────────────── */
export const BUCKETS = {
  'body-missing-row': {
    label: 'Needs a registry entry',
    short: 'needs a registry entry',
    why: 'A real, populated body name the registry does not hold under any spelling. '
      + 'This is the Pac-12 shape: the records exist, sometimes a rep is accountable for '
      + 'them, and until this card existed nothing in the product could reach them.',
    fix: 'Add the registry entry. Acquisition of the titles themselves is a different lane.',
  },
  alias: {
    label: 'Already in the registry, spelled differently',
    short: 'spelling drift, not a missing body',
    why: 'The registry already holds this body under another spelling, so this is not a '
      + 'missing body and there is nothing to acquire. GAPPS read as a zero for an '
      + 'ampersand and 46 duplicates were nearly created off the back of it.',
    fix: 'Add the alias to the row that already exists. Do NOT create a second body.',
  },
  promoter: {
    label: 'An event promoter or brand, not a governing body',
    short: 'promoter or brand',
    why: 'An event producer, series or brand rather than a sanctioning body. The records '
      + 'are real and need somewhere to be worked, so they get a card — but this card does '
      + 'not claim the promoter crowns a title, because it does not.',
    fix: 'Work the records from here. A registry entry for a promoter would say something untrue.',
  },
  unrateable: {
    label: 'Holds records, but nothing ring-worthy',
    short: 'nothing ring-worthy behind it',
    why: 'Every record under this label is unrateable — the championship name is either the '
      + "body's own name or placeholder text. It would pass any record-count test and holds "
      + 'nothing a team could buy a ring for.',
    fix: 'A data repair on the records. A registry entry would not change what is behind them.',
  },
  fused: {
    label: 'BLOCKED — one key, more than one jurisdiction',
    short: 'blocked: fused jurisdictions',
    why: 'This single key carries records from more than one state, and the acronym is a '
      + 'known collider. One card cannot honestly represent two associations: a rep sorting '
      + 'by body could not tell which state they were looking at.',
    fix: 'A data repair on the key itself. This is NOT a missing body and NOT a card to create.',
  },
}

/**
 * @param records normalized championships (each carrying `.raw`)
 * @param registry { entries: [{name, acronym, tab}], labels: Set<string>, entityCount }
 */
export function derivedBodies(records, registry) {
  const regEntries = registry?.entries || []
  const regLabels = registry?.labels || new Set()
  const cov = bodyCoverage(records)

  /* One extra pass for the facts bodyCoverage does not carry: the unrateable
   * count for the WHOLE body (its sampled rows are capped at 300, so counting
   * off them would undercount exactly the biggest cards) and which
   * governing_body values sit under each label. */
  const extra = new Map()
  for (const c of records || []) {
    const raw = c?.raw || c || {}
    const label = S(raw.sr_conference)
    if (!label || label === 'Unspecified') continue
    let e = extra.get(label)
    if (!e) extra.set(label, e = { unrateable: 0, kinds: new Map(), gb: new Map() })
    const u = unrateableKind(raw)
    if (u) { e.unrateable++; e.kinds.set(u, (e.kinds.get(u) || 0) + 1) }
    const gb = S(raw.governing_body)
    if (gb) e.gb.set(gb, (e.gb.get(gb) || 0) + 1)
  }

  const cards = cov.noCard.map((r) => {
    const x = extra.get(r.body) || { unrateable: 0, kinds: new Map(), gb: new Map() }
    const ck = collisionKey(r.body)
    const fused = !!ck && r.states.length > 1
    const alias = fused ? null : aliasOf(r.body, regEntries)
    const allUnrateable = r.count > 0 && x.unrateable === r.count
    const bucket = fused ? 'fused'
      : allUnrateable ? 'unrateable'
        : alias ? 'alias'
          : isPromoter(r.body) ? 'promoter'
            : 'body-missing-row'
    return {
      ...r,
      bucket,
      alias,
      collision: ck || null,
      fused,
      unrateable: x.unrateable,
      unrateableKinds: [...x.kinds.entries()].sort((a, b) => b[1] - a[1]),
      governingBodies: [...x.gb.entries()].sort((a, b) => b[1] - a[1]),
      substringRisk: substringRisk(r.body, regLabels),
    }
  })

  const sum = (a) => a.reduce((n, r) => n + r.count, 0)
  const buckets = Object.keys(BUCKETS).map((key) => {
    const rows = cards.filter((c) => c.bucket === key)
    return {
      key,
      ...BUCKETS[key],
      bodies: rows.length,
      records: sum(rows),
      unrateable: rows.reduce((n, r) => n + r.unrateable, 0),
      crowned: rows.reduce((n, r) => n + r.crowned, 0),
      noRepBodies: rows.filter((r) => !r.reps.length).length,
      noRepRecords: sum(rows.filter((r) => !r.reps.length)),
      rows,
    }
  })

  /* ── the other axis, measured and SAID ─────────────────────────────────── *
   * 🔴 `governing_body` fuses jurisdictions on three keys. These must render as
   * BLOCKED with the reason — never as one card, and never as missing. They are
   * not in the 467 at all, because the card join does not read this field; a
   * surface that stayed silent about them would be hiding the repair. */
  const gbTally = new Map()
  for (const c of records || []) {
    const raw = c?.raw || c || {}
    const v = S(raw.governing_body)
    if (!v) continue
    let e = gbTally.get(v)
    if (!e) gbTally.set(v, e = { label: v, count: 0, states: new Map() })
    e.count++
    const st = S(raw.sr_state_location)
    if (st) e.states.set(st, (e.states.get(st) || 0) + 1)
  }
  const fusedKeys = [...gbTally.values()]
    .map((e) => ({ label: e.label, count: e.count, collision: collisionKey(e.label), states: [...e.states.entries()].sort((a, b) => b[1] - a[1]) }))
    .filter((e) => e.collision && e.states.length > 1)
    .sort((a, b) => b.count - a.count)

  /* 🔴 AND A FALSE MATCH THAT IS ALREADY LIVE. `registryCardFor()` falls back to
   * `bareOf()`, so a qualifier-stripped label reaches a card — which is right
   * for most of them and WRONG where the acronym is a collider. These rows are
   * counted as "on a card" today and some of them are on the wrong one. Named
   * rather than fixed: re-pointing a card is a data decision. */
  const misCarded = cov.onCard
    .filter((r) => !regLabels.has(L(r.body)) && collisionKey(r.body))
    .map((r) => ({ body: r.body, count: r.count, card: r.card, collision: collisionKey(r.body), states: r.states }))
    .sort((a, b) => b.count - a.count)

  return {
    cards,
    buckets,
    fusedKeys,
    misCarded,
    totals: {
      ...cov.totals,
      derivedCards: cards.length,
      derivedRecords: sum(cards),
      unrateableOnDerived: cards.reduce((n, r) => n + r.unrateable, 0),
      noRepDerivedBodies: cards.filter((r) => !r.reps.length).length,
      noRepDerivedRecords: sum(cards.filter((r) => !r.reps.length)),
      fusedKeyCount: fusedKeys.length,
      fusedKeyRecords: fusedKeys.reduce((n, r) => n + r.count, 0),
      misCardedBodies: misCarded.length,
      misCardedRecords: misCarded.reduce((n, r) => n + r.count, 0),
    },
    /* The property, not a claim — carried straight through from bodyCoverage so
     * the two surfaces can never give two answers. */
    reconciled: cov.reconciled,
    shortfall: cov.shortfall,
  }
}
